import {
   NavigationGuardNext,
   RouteLocationNormalized,
   RouteLocationRaw,
} from "vue-router"
import {
   getAuthData,
   getOrg,
   hasActiveStripeSubscription,
   getNumberOfOrgs,
   hasInactiveSubscription,
} from "@/services/clerk"
import { addBreadcrumb } from "@/services/sentry"
import { useProjectStore } from "@stores/projectStore"
import { useOnboardingStore } from "@stores/onboardingStore"

// Guard result types
export type GuardResult = {
   continue: boolean
   redirectTo?: RouteLocationRaw
   error?: Error
}

export type GuardFunction = (
   to: RouteLocationNormalized,
   from: RouteLocationNormalized,
) => Promise<GuardResult>

// Higher-order function to compose multiple guards
export const composeGuards = (...guards: GuardFunction[]) => {
   return async (
      to: RouteLocationNormalized,
      from: RouteLocationNormalized,
      next: NavigationGuardNext,
   ) => {
      addBreadcrumb({
         category: "Route Guard",
         message: `Running composed guards for route ${to.fullPath}`,
         level: "info",
      })

      // Execute each guard in sequence
      for (const guard of guards) {
         const result = await guard(to, from)
         if (!result.continue) {
            if (result.error) {
               console.error(result.error)
            }

            if (result.redirectTo) {
               return next(result.redirectTo)
            }

            return next(false)
         }
      }

      return next()
   }
}

// Basic guard for validating user is logged in
export const requireLogin: GuardFunction = async (to, _from) => {
   addBreadcrumb({
      category: "Route Guard",
      message: `Validating user is logged in for route ${to.fullPath}`,
      level: "info",
   })

   const { sessionActive } = await getAuthData()

   if (!sessionActive) {
      if (
         to.name === "SignIn" ||
         to.name === "SignUp" ||
         to.name?.toString().startsWith("SignIn") ||
         to.name === "VerifyEmailAddress"
      ) {
         return {continue: true}
      }
      return {
         continue: false,
         redirectTo: { name: "SignIn" },
      }
   }

   return { continue: true }
}

// Guard for validating active organization
export const requireActiveOrg: GuardFunction = async (to, _from) => {
   addBreadcrumb({
      category: "Route Guard",
      message: `Validating active organization for route ${to.fullPath}`,
      level: "info",
   })

   const { decodedToken } = await getAuthData()

   if (!decodedToken || !decodedToken.org_id) {
      return {
         continue: false,
         redirectTo: { name: "SelectOrg" },
      }
   }

   return { continue: true }
}

export const requireNoOrg: GuardFunction = async (to, _from) => {
   addBreadcrumb({
      category: "Route Guard",
      message: `Checking if user has orgs for ${to.fullPath}`,
      level: "info",
   })
   // Check if user has no orgs and redirect to create org
   const numOrgs = await getNumberOfOrgs()
   if (numOrgs === 0) {
      return { continue: true }
   }

   return {
      continue: false,
      redirectTo: { name: "SelectOrg" }
   }
}

export const requireAnOrg: GuardFunction = async (to, _from) => {
   addBreadcrumb({
      category: "Route Guard",
      message: `Checking if user has orgs for ${to.fullPath}`,
      level: "info",
   })
   // Check if user has no orgs and redirect to create org
   const numOrgs = await getNumberOfOrgs()
   if (numOrgs === 0) {
      return {
         continue: false,
         redirectTo: { name: "OnboardingCreateOrg" },
      }
   }

   // Allow users to select org regardless of subscription status
   // They'll be prompted for subscription when accessing protected features
   return { continue: true }
}

// Guard for validating stripe subscription
export const requireActiveSubscription: GuardFunction = async (to, _from) => {
   addBreadcrumb({
      category: "Route Guard",
      message: `Validating active subscription for route ${to.fullPath}`,
      level: "info",
   })

   const hasSubscription = await hasActiveStripeSubscription()

   if (!hasSubscription) {
      const inactiveSub = await hasInactiveSubscription()

      if (inactiveSub) {
         return {
            continue: false,
            redirectTo: { name: "SubscriptionError" },
         }
      }

      // No active subscription - redirect to select subscription
      return {
         continue: false,
         redirectTo: { name: "SelectSubscription" },
      }
   }

   return { continue: true }
}

// Guard for validating employee status
export const requireEzbotEmployee: GuardFunction = async (to, _from) => {
   addBreadcrumb({
      category: "Route Guard",
      message: `Validating user is Ezbot employee for route ${to.fullPath}`,
      level: "info",
   })

   // In development mode, treat all users as employees if the flag is set
   if (import.meta.env.VITE_MOCK_EMPLOYEE === "true") {
      console.log(
         "Development mode: Treating user as Ezbot employee in route guard",
      )
      return { continue: true }
   }

   const { decodedToken } = await getAuthData()

   if (!decodedToken || decodedToken.user_metadata?.isEzbotEmployee !== true) {
      return {
         continue: false,
         redirectTo: { name: "Home" },
      }
   }

   return { continue: true }
}

// Guard for validating organization slug matches current organization
export const validateOrgSlug: GuardFunction = async (to, _from) => {
   const routeOrgSlug = to.params.orgSlug as string

   if (!routeOrgSlug) {
      return { continue: true }
   }

   const org = await getOrg()

   if (!org) {
      return {
         continue: false,
         redirectTo: { name: "SelectOrg" },
      }
   }

   if (org.slug !== routeOrgSlug) {
      return {
         continue: false,
         redirectTo: {
            name: to.name as string,
            params: { ...to.params, orgSlug: org.slug },
         },
      }
   }

   return { continue: true }
}

// Guard for validating project ID
export const requireValidProject: GuardFunction = async (to, _from) => {
   addBreadcrumb({
      category: "Route Guard",
      message: `Validating project for route ${to.fullPath}`,
      level: "info",
   })

   const projectStore = useProjectStore()
   const projectId = to.params.projectId ? Number(to.params.projectId) : null
   const ownsProject = await projectStore.activeOrgHasCurrentProject
   // If no projectId in route, handle redirection
   if (!projectId || !ownsProject) {
      const org = await getOrg()
      if (!org) {
         return {
            continue: false,
            redirectTo: { name: "SelectOrg" },
         }
      }

      const { slug } = org

      // If we don't have projects in the store, fetch them
      try {
         await projectStore.getActiveOrgProjects()
         const projects = projectStore.projects

         if (!projects || projects.length === 0) {
            return {
               continue: false,
               redirectTo: { name: "CreateProject", params: { orgSlug: slug } },
            }
         }

         return {
            continue: false,
            redirectTo: {
               name: "ProjectHome",
               params: { projectId: projects[0].id, orgSlug: slug },
            },
         }
      } catch (error) {
         return {
            continue: false,
            redirectTo: { name: "OrgHome", params: { orgSlug: slug } },
            error: error as Error,
         }
      }
   }

   // Set the current projectId in the store
   projectStore.projectId = projectId

   // If we already have projects in the store, check if the current project exists
   if (projectStore.projects.length > 0) {
      const projectExists = projectStore.projects.some(
         (p) => p.id === projectId,
      )

      if (projectExists) {
         return { continue: true }
      }
   }

   // If we don't have projects or the current project doesn't exist in our list,
   // fetch projects from the backend (this will also validate if the user has access)
   try {
      await projectStore.getActiveOrgProjects()
      const projectExists = projectStore.projects.some(
         (p) => p.id === projectId,
      )

      if (!projectExists) {
         // Project doesn't exist or user doesn't have access
         const org = await getOrg()
         if (!org) {
            return {
               continue: false,
               redirectTo: { name: "SelectOrg" },
            }
         }

         const { slug } = org

         if (projectStore.projects.length > 0) {
            return {
               continue: false,
               redirectTo: {
                  name: "ProjectHome",
                  params: {
                     projectId: projectStore.projects[0].id,
                     orgSlug: slug,
                  },
               },
            }
         } else {
            return {
               continue: false,
               redirectTo: { name: "CreateProject", params: { orgSlug: slug } },
            }
         }
      }

      return { continue: true }
   } catch (error) {
      // Handle API errors (e.g., unauthorized)
      const org = await getOrg()
      if (!org) {
         return {
            continue: false,
            redirectTo: { name: "SelectOrg" },
         }
      }

      const { slug } = org

      return {
         continue: false,
         redirectTo: { name: "OrgHome", params: { orgSlug: slug } },
         error: error as Error,
      }
   }
}

export const requireOnboarded: GuardFunction = async (to, _from) => {
   addBreadcrumb({
      category: "Route Guard",
      message: `Validating onboarding data for route ${to.fullPath}`,
      level: "info",
   })

   const onboardingStore = useOnboardingStore()
   await onboardingStore.fetchProjectOnboarding()
   const { dataReceived, rewardReceived } = onboardingStore
   if (!dataReceived || !rewardReceived) {
      return {
         continue: false,
         redirectTo: {
            name: "OnboardingHome",
            params: {
               orgSlug: to.params.orgSlug as string,
               projectId: to.params.projectId as string,
            },
         },
      }
   }

   return { continue: true }
}

// Guard for CreateProject route - redirects to existing project if one exists
// unless user explicitly wants to create a new project (via query param)
export const requireNoExistingProject: GuardFunction = async (to, _from) => {
   addBreadcrumb({
      category: "Route Guard",
      message: `Checking for existing projects before allowing CreateProject for route ${to.fullPath}`,
      level: "info",
   })

   // Allow intentional project creation (e.g., from ProjectPicker dropdown)
   if (to.query.new === 'true') {
      return { continue: true }
   }

   const projectStore = useProjectStore()
   const org = await getOrg()
   
   if (!org) {
      return {
         continue: false,
         redirectTo: { name: "SelectOrg" },
      }
   }

   try {
      await projectStore.getActiveOrgProjects()
      const projects = projectStore.projects

      // If user already has a project, redirect to it instead of CreateProject
      // (unless they explicitly requested to create a new one)
      if (projects && projects.length > 0) {
         return {
            continue: false,
            redirectTo: {
               name: "ProjectHome",
               params: { projectId: projects[0].id, orgSlug: org.slug },
            },
         }
      }

      // No projects exist, allow CreateProject
      return { continue: true }
   } catch (_error) {
      // On error, allow CreateProject to proceed
      return { continue: true }
   }
}

// Common guard combinations
export const authGuard = composeGuards(requireLogin)
export const selectOrgGuard = composeGuards(requireLogin, requireAnOrg)
export const orgGuard = composeGuards(
   requireLogin,
   requireActiveOrg,
   validateOrgSlug,
)
export const subscriptionGuard = composeGuards(
   requireLogin,
   requireActiveOrg,
   requireActiveSubscription,
)
export const createProjectGuard = composeGuards(
   requireLogin,
   requireActiveOrg,
   requireActiveSubscription,
   requireNoExistingProject,
)
export const projectGuard = composeGuards(
   requireLogin,
   requireActiveOrg,
   validateOrgSlug,
   requireValidProject,
)
export const subscriptionProjectGuard = composeGuards(
   requireLogin,
   requireActiveOrg,
   requireActiveSubscription,
   validateOrgSlug,
   requireValidProject,
)
export const employeeGuard = composeGuards(requireLogin, requireEzbotEmployee)
export const createOrgGuard = composeGuards(requireLogin, requireNoOrg)
export const onboardedGuard = composeGuards(
   requireLogin,
   requireActiveOrg,
   requireActiveSubscription,
   validateOrgSlug,
   requireValidProject,
   requireOnboarded,
)